import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'
import { Order } from '../types'
import { formatRoiInsight, formatRoiValue } from '../utils/roi-copy'

const c = {
  dark: '#1a1a2e',
  text: '#2d2d3f',
  muted: '#8b8ba3',
  accent: '#7c3aed',
  accentLight: '#ede9fe',
  green: '#059669',
  greenLight: '#d1fae5',
  red: '#dc2626',
  redLight: '#fee2e2',
  bg: '#fafafa',
  card: '#ffffff',
  border: '#eeeef2'
}

const s = StyleSheet.create({
  page: {
    padding: 48,
    fontFamily: 'Helvetica',
    backgroundColor: c.bg
  },
  hero: {
    backgroundColor: c.dark,
    borderRadius: 8,
    padding: 28,
    marginBottom: 28
  },
  heroLabel: {
    fontSize: 9,
    color: c.accent,
    textTransform: 'uppercase',
    letterSpacing: 2,
    marginBottom: 8,
    fontWeight: 'bold'
  },
  heroTitle: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 6
  },
  heroSub: {
    fontSize: 11,
    color: '#a0a0b8'
  },
  insight: {
    backgroundColor: c.accentLight,
    borderRadius: 6,
    padding: 16,
    marginBottom: 24,
    borderLeftWidth: 3,
    borderLeftColor: c.accent
  },
  insightLabel: {
    fontSize: 8,
    color: c.accent,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    fontWeight: 'bold',
    marginBottom: 6
  },
  insightText: {
    fontSize: 11,
    color: c.text,
    lineHeight: 1.6
  },
  section: { marginBottom: 24 },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: c.muted,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginBottom: 12
  },
  kpiRow: { flexDirection: 'row', gap: 10 },
  kpiCard: {
    flex: 1,
    backgroundColor: c.card,
    borderRadius: 6,
    padding: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: c.border
  },
  kpiLabel: {
    fontSize: 7,
    color: c.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 6,
    textAlign: 'center'
  },
  kpiValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: c.text,
    textAlign: 'center'
  },
  kpiSub: {
    fontSize: 8,
    color: c.muted,
    marginTop: 4,
    textAlign: 'center'
  },
  tmHighlight: {
    flexDirection: 'row',
    gap: 12
  },
  tmCard: {
    flex: 1,
    borderRadius: 6,
    padding: 18,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: c.border
  },
  tmValue: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center'
  },
  tmLabel: {
    fontSize: 7,
    color: c.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 8,
    textAlign: 'center'
  },
  tmDiffLabel: {
    fontSize: 8,
    color: c.muted,
    marginTop: 4,
    textAlign: 'center'
  },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 48,
    right: 48,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: c.border,
    paddingTop: 8
  },
  footerText: { fontSize: 7, color: '#c4c4d4' }
})

function fmt(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0)
}

function fmtCompact(value: number): string {
  const abs = Math.abs(value || 0)
  if (abs >= 1_000_000_000) {
    return `R$ ${(value / 1_000_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} bi`
  }
  if (abs >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} mi`
  }
  return fmt(value)
}

interface CompanyStats {
  totalRevenue: number
  widdeCount: number
  influencedRevenue: number
  directCount: number
}

interface RoiData {
  roi: number | null
  proportionalCost: number
}

interface Props {
  companyTitle: string
  stats: CompanyStats
  allOrders: Order[]
  roiData: RoiData | null
  startDate: string
  endDate: string
}

export function CompanyReportPdf({
  companyTitle,
  stats,
  allOrders,
  roiData,
  startDate,
  endDate
}: Props) {
  const fmtDate = (d: string) => {
    const [y, m, day] = d.split('-')
    return `${day}/${m}/${y}`
  }
  const days =
    Math.round(
      (new Date(endDate).getTime() - new Date(startDate).getTime()) /
        (1000 * 60 * 60 * 24)
    ) + 1

  const totalOrders = allOrders.length
  const influencedOrders = allOrders.filter(o => o.widdeInfluenciou)
  // Valor CHEIO dos pedidos atribuídos — mesma unidade do organico, é o que
  // entra na comparação/uplift (FDN-1939: comparar com o item-level gerava
  // uplift espúrio em lojas de cesta grande).
  const attributedOrdersRevenue = influencedOrders.reduce(
    (sum, o) => sum + (o.purchaseRevenue || 0),
    0
  )
  const organicOrders = allOrders.filter(o => !o.widdeInfluenciou)
  const organicRevenue = organicOrders.reduce(
    (sum, o) => sum + (o.purchaseRevenue || 0),
    0
  )

  const avgTicketAttributedOrders =
    influencedOrders.length > 0
      ? attributedOrdersRevenue / influencedOrders.length
      : 0
  const avgTicketOrganic =
    organicOrders.length > 0 ? organicRevenue / organicOrders.length : 0
  const diffPercent =
    avgTicketOrganic > 0
      ? ((avgTicketAttributedOrders - avgTicketOrganic) / avgTicketOrganic) * 100
      : null

  const conversionRate =
    totalOrders > 0 ? (influencedOrders.length / totalOrders) * 100 : 0

  // Build insight narrative
  const insights: string[] = []
  if (conversionRate > 0) {
    insights.push(
      `Em ${days} dias, ${conversionRate.toFixed(1)}% dos pedidos foram atribuídos à Widde.`
    )
  }
  if (diffPercent !== null && diffPercent > 0) {
    insights.push(
      `Clientes que interagiram com a Widde gastam em media ${diffPercent.toFixed(0)}% a mais por pedido (${fmt(avgTicketAttributedOrders)} vs ${fmt(avgTicketOrganic)}).`
    )
  }
  if (roiData?.roi !== null && roiData?.roi !== undefined && roiData.roi > 1) {
    insights.push(
      formatRoiInsight(roiData.roi)
    )
  }

  return (
    <Document>
      <Page size="A4" style={s.page}>
        {/* Hero */}
        <View style={s.hero}>
          <Text style={s.heroLabel}>Relatorio de Performance</Text>
          <Text style={s.heroTitle}>{companyTitle}</Text>
          <Text style={s.heroSub}>
            {fmtDate(startDate)} a {fmtDate(endDate)} | {days} dias
          </Text>
        </View>

        {/* Insight */}
        {insights.length > 0 && (
          <View style={s.insight}>
            <Text style={s.insightLabel}>Destaque</Text>
            <Text style={s.insightText}>{insights.join(' ')}</Text>
          </View>
        )}

        {/* Resultados Gerais */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>Resultados Gerais</Text>
          <View style={s.kpiRow}>
            <View style={s.kpiCard}>
              <Text style={s.kpiLabel}>Receita Total</Text>
              <Text style={s.kpiValue}>{fmtCompact(stats.totalRevenue)}</Text>
              <Text style={s.kpiSub}>{totalOrders} pedidos</Text>
            </View>
            <View
              style={{ ...s.kpiCard, borderColor: c.accent, borderWidth: 1.5 }}
            >
              <Text style={{ ...s.kpiLabel, color: c.accent }}>
                Receita Atribuída
              </Text>
              <Text style={{ ...s.kpiValue, color: c.accent }}>
                {stats.influencedRevenue > 0
                  ? fmtCompact(stats.influencedRevenue)
                  : '\u2014'}
              </Text>
              <Text style={s.kpiSub}>{stats.widdeCount} pedidos</Text>
            </View>
            <View
              style={{
                ...s.kpiCard,
                backgroundColor:
                  roiData?.roi !== null &&
                  roiData?.roi !== undefined &&
                  roiData.roi >= 1
                    ? c.greenLight
                    : c.card,
                borderColor:
                  roiData?.roi !== null &&
                  roiData?.roi !== undefined &&
                  roiData.roi >= 1
                    ? c.green
                    : c.border,
                borderWidth:
                  roiData?.roi !== null &&
                  roiData?.roi !== undefined &&
                  roiData.roi >= 1
                    ? 1.5
                    : 1
              }}
            >
              <Text style={s.kpiLabel}>ROI Widde</Text>
              <Text
                style={{
                  ...s.kpiValue,
                  color:
                    roiData?.roi !== null && roiData?.roi !== undefined
                      ? roiData.roi >= 0
                        ? c.green
                        : c.red
                      : c.muted
                }}
              >
                {roiData?.roi !== null && roiData?.roi !== undefined
                  ? `${formatRoiValue(roiData.roi)}x`
                  : '\u2014'}
              </Text>
              {roiData && roiData.proportionalCost > 0 && (
                <Text style={s.kpiSub}>
                  Custo: {fmtCompact(roiData.proportionalCost)}
                </Text>
              )}
            </View>
          </View>
        </View>

        {/* Ticket Medio */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>Ticket Medio: Widde vs Organico</Text>
          <View style={s.tmHighlight}>
            <View
              style={{
                ...s.tmCard,
                backgroundColor: c.accentLight,
                borderColor: c.accent,
                borderWidth: 1.5
              }}
            >
              <Text style={{ ...s.tmLabel, color: c.accent }}>Com Widde</Text>
              <Text style={{ ...s.tmValue, color: c.accent }}>
                {fmt(avgTicketAttributedOrders)}
              </Text>
            </View>
            <View style={{ ...s.tmCard, backgroundColor: c.card }}>
              <Text style={s.tmLabel}>Sem Widde</Text>
              <Text style={{ ...s.tmValue, color: c.text }}>
                {fmt(avgTicketOrganic)}
              </Text>
            </View>
            <View
              style={{
                ...s.tmCard,
                backgroundColor:
                  diffPercent !== null && diffPercent >= 0
                    ? c.greenLight
                    : c.redLight,
                borderColor:
                  diffPercent !== null && diffPercent >= 0 ? c.green : c.red,
                borderWidth: 1.5
              }}
            >
              <Text style={s.tmLabel}>Diferenca</Text>
              <Text
                style={{
                  ...s.tmValue,
                  color:
                    diffPercent !== null
                      ? diffPercent >= 0
                        ? c.green
                        : c.red
                      : c.muted
                }}
              >
                {diffPercent !== null
                  ? `${diffPercent >= 0 ? '+' : ''}${diffPercent.toFixed(1)}%`
                  : '\u2014'}
              </Text>
              <Text style={s.tmDiffLabel}>por pedido</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={s.footer}>
          <Text style={s.footerText}>Widde Video Commerce</Text>
          <Text style={s.footerText}>
            Relatorio gerado automaticamente | {fmtDate(startDate)} -{' '}
            {fmtDate(endDate)}
          </Text>
        </View>
      </Page>
    </Document>
  )
}
